import { Component, OnDestroy, OnInit } from '@angular/core';
import { User, UserService } from '../../../services/user.service';
import { AuthService } from '../../../services/auth.service';
import { NavigationService } from '../../../services/navigation.service';
import { getExceptionMessage } from '../../../../shared/functions/get-exception-message';
import { catchError, finalize, tap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'hfc-verify-phone-number',
  templateUrl: './verify-phone-number.component.html',
  styleUrls: ['./verify-phone-number.component.scss']
})
export class VerifyPhoneNumberComponent implements OnInit, OnDestroy {
  public isSubmitting = false;
  public error: string;
  public isInputRed = false;

  // resend UI state
  public isRequestNewCodeRed = false;
  public isRequestNewCodeLoading = false;
  public requestMessage: string | null = null;

  public remainingSeconds = 30;
  public canRequestNewCode = false;

  private lastSentAt = new Date();
  private countdownHandle: any;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private navigationService: NavigationService
  ) {}

  public get user() {
    return this.userService.user;
  }

  ngOnInit(): void {
    this.startOrRefreshCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
    }
  }

  public async onCodeEntered(code: string) {
    this.isSubmitting = true;
    this.isInputRed = false;

    this.authService
      .verifySmsCodeObservable(code)
      .pipe(
        tap((userSession: User) => {
          this.userService.setUser(userSession);
          this.navigationService.goToDashboard();
        }),
        catchError((error) => {
          this.isInputRed = true;
          this.error = error?.error?.message ?? 'Invalid verification code. Please try again.';
          return of(null);
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe();
  }

  public async onRequestNewClicked() {
    const now = new Date();
    const secondsSinceLast = (now.getTime() - this.lastSentAt.getTime()) / 1000;

    if (secondsSinceLast < 30) {
      this.isRequestNewCodeRed = true;
      this.requestMessage = 'Please wait 30 seconds before requesting another code';
      return;
    }

    if (!navigator.onLine) {
      this.isRequestNewCodeRed = true;
      this.requestMessage = 'No internet connection. Please check your network and try again.';
      return;
    }

    this.isRequestNewCodeRed = false;
    this.requestMessage = null;
    this.isRequestNewCodeLoading = true;

    this.authService
      .requestNewSmsCodeObservable()
      .pipe(
        catchError((error) => {
          // status 0
          if (error.status === 0 || !navigator.onLine) {
            this.error = 'An error occurred resending the code. Please try again.';
          } else {
            this.error = getExceptionMessage(error) || 'An error occurred resending the code. Please try again.';
          }

          this.isRequestNewCodeRed = true;
          this.requestMessage = this.error;

          return of(null);
        }),
        finalize(() => {
          this.isRequestNewCodeLoading = false;
        })
      )
      .subscribe(() => {
        this.requestMessage = 'A new code has been sent.';
        this.isRequestNewCodeRed = false;
        this.lastSentAt = new Date();
        this.startOrRefreshCountdown();
      });
  }

  private startOrRefreshCountdown() {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
    }
    this.tickCountdown(); // compute immediately for UI
    this.countdownHandle = setInterval(() => this.tickCountdown(), 1000);
  }

  private tickCountdown() {
    const elapsed = (Date.now() - this.lastSentAt.getTime()) / 1000;
    const remaining = Math.max(0, Math.ceil(30 - elapsed));
    this.remainingSeconds = remaining;
    this.canRequestNewCode = remaining === 0;
  }
}
